import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  checkReading,
  daysBetween,
  expectedPerDayFor,
  hasErrors,
  METER_ROUND_STATUS_LABELS,
  METER_TYPE_LABELS,
  needsRemark,
  READING_EVENT_LABELS,
  READING_EVENTS,
  roundRegisterRows,
  todayIso,
  usagePeriods,
  type MeterRoundDetailDto,
  type ReadingEvent,
  type RoundMeterDto,
} from "@klantpunt/shared";
import { IconAlert, IconCamera, IconCheck, IconChevronLeft, IconDownload, IconFile } from "../app/icons";
import { Tabs } from "../app/Choice";
import { Modal } from "../app/Modal";
import { fmtDate, nl, parseNumber } from "../features/meters/MeterSection";
import { useRoundPdf } from "../features/meters/useRoundPdf";
import { roundKey, useMeterRound, usePendingRoundOps, useRoundDetail } from "../features/meters/useMeterRound";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { photoUrl, thumbUrl } from "../lib/photos";
import { useToast } from "../lib/toast";
import { useBuildingContext } from "../lib/buildingContext";
import { PHONE_QUERY, useMediaQuery } from "../lib/useMediaQuery";

/**
 * Opnameronde (docs/METERSTANDEN.md §8): per meter de stand per telwerk met foto, direct
 * gecontroleerd (§5), ook offline; daarna controle (hoofd- tegen tussenmeters) en afronden.
 */
const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

type Status = "todo" | "done" | "unreachable" | "flagged";
const STATUS_LABELS: Record<Status, string> = {
  todo: "Te doen",
  done: "Opgenomen",
  unreachable: "Niet bereikbaar",
  flagged: "Afwijking",
};
const statusOf = (m: RoundMeterDto): Status =>
  !m.reading ? "todo" : m.reading.unreachable ? "unreachable" : m.check && m.check.issues.length ? "flagged" : "done";

export function MeterRoundPage() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  if (!hasModule(me, "meters"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Meterstanden.</p>
      </div>
    );
  return <Round id={id} canEdit={hasModule(me, "meters", "edit")} />;
}

function Round({ id, canEdit }: { id: string; canEdit: boolean }) {
  const q = useRoundDetail(id);
  const pdf = useRoundPdf();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<"meters" | "review">("meters");
  const [open, setOpen] = useState<string | null>(params.get("meter"));
  const [busy, setBusy] = useState(false);
  const pending = usePendingRoundOps(id);
  const phone = useMediaQuery(PHONE_QUERY);
  // De ronde hoort bij één gebouw: dat gebouw ook in de bovenbalk.
  const { id: ctxBuilding, setBuilding } = useBuildingContext();
  const roundBuilding = q.data?.building.id;
  useEffect(() => {
    if (roundBuilding && roundBuilding !== ctxBuilding) setBuilding(roundBuilding);
  }, [roundBuilding, ctxBuilding, setBuilding]);
  useEffect(() => {
    if (!params.get("meter")) return;
    params.delete("meter");
    setParams(params, { replace: true });
  }, [params, setParams]);
  const d = q.data;
  if (q.isPending) return <Loading page />;
  if (!d) return <p className="center">Deze ronde bestaat niet (meer).</p>;
  const r = d.round;
  const editable = canEdit && r.status === "open";
  const current = d.meters.find((m) => m.asset.id === open);
  const todo = d.meters.filter((m) => !m.reading).length;

  /** Na opslaan: de volgende meter die nog moet (in lijstvolgorde). */
  const next = (after: string) => {
    const k = d.meters.findIndex((m) => m.asset.id === after);
    const rest = [...d.meters.slice(k + 1), ...d.meters.slice(0, k)];
    return rest.find((m) => !m.reading)?.asset.id ?? null;
  };

  async function reopen() {
    if (!window.confirm("Ronde heropenen? Daarna kun je meters alsnog opnemen of standen aanpassen.")) return;
    setBusy(true);
    try {
      const res = await api<MeterRoundDetailDto>(`/meters/rounds/${id}/reopen`, { method: "POST" });
      qc.setQueryData(roundKey(id), res);
      toast("Ronde heropend");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    setBusy(true);
    try {
      const res = await api<MeterRoundDetailDto>(`/meters/rounds/${id}/complete`, { method: "POST" });
      qc.setQueryData(roundKey(id), res);
      toast("Ronde afgerond");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page meter-round">
      <Link to="/meterstanden" className="btn ghost small">
        <IconChevronLeft size={18} aria-hidden="true" />
        Meterstanden
      </Link>
      <p className="label">
        {d.building.code} · {d.building.name}
      </p>
      <div className="titlerow">
        <h1 className="dot">{r.name}</h1>
        <span className="chip">{METER_ROUND_STATUS_LABELS[r.status]}</span>
        <span className="btnrow">
          <button type="button" className="btn" disabled={pdf.busy === id} onClick={() => void pdf.make(id)}>
            <IconFile size={20} aria-hidden="true" />
            {pdf.busy === id ? "PDF maken…" : "Rapport (PDF)"}
          </button>
          <a className="btn" href={`/api/meters/rounds/${id}/export.xlsx`} download>
            <IconDownload size={20} aria-hidden="true" />
            Excel
          </a>
          {editable && (
            <button
              type="button"
              className="btn primary"
              disabled={busy || todo > 0 || pending > 0}
              onClick={() => void complete()}
            >
              <IconCheck size={20} aria-hidden="true" />
              Afronden
            </button>
          )}
          {canEdit && r.status === "completed" && (
            <button type="button" className="btn" disabled={busy} onClick={() => void reopen()}>
              Heropenen
            </button>
          )}
        </span>
      </div>
      <p className="muted small">
        {fmtDate(r.periodStart)} t/m {fmtDate(r.periodEnd)} · {d.meters.length - todo} van {d.meters.length} opgenomen
        {r.unreachable > 0 && ` · ${r.unreachable} niet bereikbaar`}
        {editable && todo > 0 && " · afronden kan als elke meter is opgenomen"}
      </p>
      <div
        className="progress"
        role="progressbar"
        aria-label="Voortgang"
        aria-valuemin={0}
        aria-valuemax={d.meters.length}
        aria-valuenow={d.meters.length - todo}
      >
        <span style={{ width: `${d.meters.length ? ((d.meters.length - todo) / d.meters.length) * 100 : 0}%` }} />
      </div>
      {pending > 0 && (
        <p className="note" role="status">
          {pending} opname{pending === 1 ? "" : "s"} wacht{pending === 1 ? "" : "en"} op verbinding.
        </p>
      )}
      <Tabs
        label="Onderdeel"
        items={[
          { value: "meters", label: "Meters" },
          {
            value: "review",
            label: `Controle${d.children.some((c) => c.exceeds) || r.flagged ? " (!)" : ""}`,
          },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "review" ? (
        <Review d={d} onOpen={setOpen} />
      ) : phone ? (
        <MeterList d={d} onOpen={setOpen} />
      ) : (
        <MeterTable d={d} onOpen={setOpen} />
      )}
      {current && (
        <ReadingSheet
          key={current.asset.id}
          d={d}
          m={current}
          editable={editable}
          onClose={() => setOpen(null)}
          onSaved={() => setOpen(next(current.asset.id))}
        />
      )}
    </div>
  );
}

function MeterList({ d, onOpen }: { d: MeterRoundDetailDto; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState(false);
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = d.meters.filter(
      (m) =>
        (!t ||
          `${m.asset.code} ${m.asset.name} ${m.meter.ean ?? ""} ${m.meter.meterNumber ?? ""}`
            .toLowerCase()
            .includes(t)) &&
        (!only || !m.reading),
    );
    const out: { floor: string; meters: RoundMeterDto[] }[] = [];
    for (const m of list) {
      const f = m.asset.floorName ?? "Zonder verdieping";
      const g = out.find((x) => x.floor === f) ?? out[out.push({ floor: f, meters: [] }) - 1]!;
      g.meters.push(m);
    }
    return out;
  }, [d, q, only]);
  return (
    <section className="panel" aria-label="Meters in deze ronde">
      <div className="stack meterfilter">
        {/* Zoeken pas bij een flinke ronde; anders is de lijst zelf overzichtelijk genoeg. */}
        {d.meters.length >= 8 && (
          <input
            type="search"
            aria-label="Meters zoeken"
            placeholder="Zoek op code, naam, EAN of meternummer…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        )}
        <label className="check small">
          <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} />
          Alleen te doen
        </label>
      </div>
      {!d.meters.length && (
        <p className="muted">
          Dit gebouw heeft nog geen actieve meters. Voeg een asset van soort Meter toe en stel hem in.
        </p>
      )}
      {groups.map((g) => (
        <div key={g.floor}>
          <p className="label">{g.floor}</p>
          <ul className="list">
            {g.meters.map((m) => {
              const s = statusOf(m);
              return (
                <li key={m.asset.id}>
                  <button type="button" className="profile-row linkrow" onClick={() => onOpen(m.asset.id)}>
                    <span className={`meterstatus ms-${s}`} aria-hidden="true">
                      {s === "todo" ? "" : s === "flagged" ? "!" : s === "unreachable" ? "–" : "✓"}
                    </span>
                    <span className="grow">
                      <strong>
                        {m.asset.code} {m.asset.name}
                      </strong>
                      <span className="muted small block">
                        {METER_TYPE_LABELS[m.meter.meterType]}
                        {m.asset.roomName && ` · ${m.asset.roomName}`}
                        {m.vacantNow && " · leeg"} · {STATUS_LABELS[s]}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** Desktop: per meter en telwerk de standen, het verbruik en het verschil met een jaar eerder. */
function MeterTable({ d, onOpen }: { d: MeterRoundDetailDto; onOpen: (id: string) => void }) {
  if (!d.meters.length) return <MeterList d={d} onOpen={onOpen} />;
  const pct = (a: number | null, b: number | null) =>
    a != null && b ? `${a >= b ? "+" : ""}${Math.round((a / b - 1) * 100)}%` : "—";
  return (
    <section className="panel" aria-label="Meters in deze ronde">
      <div className="tscroll">
        <table className="it roundtable">
          <thead>
            <tr>
              <th scope="col">Meter</th>
              <th scope="col">Telwerk</th>
              <th scope="col" className="num">
                Vorige stand
              </th>
              <th scope="col" className="num">
                Stand
              </th>
              <th scope="col" className="num">
                Verbruik
              </th>
              <th scope="col" className="num">
                Verwacht
              </th>
              <th scope="col" className="num">
                Per dag
              </th>
              <th scope="col" className="num">
                t.o.v. vorig jaar
              </th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.meters.map((m) => {
              const s = statusOf(m);
              const rows = roundRegisterRows(m);
              const unit = m.meter.unit ? ` ${m.meter.unit}` : "";
              return rows.map((x, i) => (
                <tr key={`${m.asset.id}-${x.registerId}`} className={i ? "sub" : undefined}>
                  {i === 0 && (
                    <td rowSpan={rows.length}>
                      <button type="button" className="linkbtn" onClick={() => onOpen(m.asset.id)}>
                        <strong>
                          {m.asset.code} {m.asset.name}
                        </strong>
                      </button>
                      <span className="muted small block">
                        {METER_TYPE_LABELS[m.meter.meterType]}
                        {m.asset.floorName && ` · ${m.asset.floorName}`}
                        {m.asset.roomName && `, ${m.asset.roomName}`}
                      </span>
                    </td>
                  )}
                  <td>{rows.length > 1 ? x.registerName : ""}</td>
                  <td className="num">
                    {nl(x.previous, 2)}
                    {x.previousOn && <span className="muted small block">{fmtDate(x.previousOn)}</span>}
                  </td>
                  <td className="num">
                    {m.reading?.unreachable ? "—" : nl(x.value, 2)}
                    {x.readOn && <span className="muted small block">{fmtDate(x.readOn)}</span>}
                  </td>
                  <td className="num">{x.usage != null ? `${nl(x.usage, 0)}${unit}` : "—"}</td>
                  <td className="num">{x.expected != null ? `${nl(x.expected, 0)}${unit}` : "—"}</td>
                  <td className="num">{x.perDay != null ? `${nl(x.perDay, 2)}${unit}` : "—"}</td>
                  <td className="num">{pct(x.perDay, x.lastYearPerDay)}</td>
                  {i === 0 && (
                    <td rowSpan={rows.length}>
                      <span className={`meterstatus ms-${s}`} aria-hidden="true">
                        {s === "todo" ? "" : s === "flagged" ? "!" : s === "unreachable" ? "–" : "✓"}
                      </span>{" "}
                      {STATUS_LABELS[s]}
                    </td>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Review({ d, onOpen }: { d: MeterRoundDetailDto; onOpen: (id: string) => void }) {
  const flagged = d.meters.filter((m) => m.check && m.check.issues.length);
  const unreachable = d.meters.filter((m) => m.reading?.unreachable);
  return (
    <section className="panel stack" aria-label="Controle">
      <p className="label">Hoofdmeters en tussenmeters</p>
      {!d.children.length ? (
        <p className="muted small">
          Geen hoofdmeters met tussenmeters die allebei zijn opgenomen. Stel bij een tussenmeter de hoofdmeter in om dit
          te controleren.
        </p>
      ) : (
        <ul className="list compact">
          {d.children.map((c) => (
            <li key={c.main.id} className={`row small${c.exceeds ? " note warn" : ""}`}>
              {c.exceeds && <IconAlert size={18} aria-hidden="true" />}
              <span className="grow">
                <strong>
                  {c.main.code} {c.main.name}
                </strong>
                : hoofdmeter {nl(c.mainUsage, 1)} {c.unit}, tussenmeters samen {nl(c.sum, 1)} {c.unit} ·{" "}
                {c.exceeds
                  ? "tussenmeters samen meer dan de hoofdmeter"
                  : `onverklaard ${nl(c.unexplained, 1)} ${c.unit}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="label">Afwijkingen ({flagged.length})</p>
      {!flagged.length ? (
        <p className="muted small">Geen afwijkingen.</p>
      ) : (
        <ul className="list compact">
          {flagged.map((m) => (
            <li key={m.asset.id}>
              <button type="button" className="profile-row linkrow small" onClick={() => onOpen(m.asset.id)}>
                <span className="grow">
                  <strong>
                    {m.asset.code} {m.asset.name}
                  </strong>
                  {m.check!.issues.map((i) => (
                    <span key={`${i.code}-${i.registerId ?? ""}`} className="block">
                      {i.message}
                    </span>
                  ))}
                  {m.reading?.remark && <span className="block muted">Toelichting: {m.reading.remark}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="label">Niet bereikbaar ({unreachable.length})</p>
      {unreachable.length > 0 && (
        <ul className="list compact">
          {unreachable.map((m) => (
            <li key={m.asset.id} className="small">
              {m.asset.code} {m.asset.name}: {m.reading!.unreachableReason}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type ValDraft = { value: string; oldFinal: string; newStart: string; photo: string | null };

/**
 * Optionele foto van één telwerk, voor een display dat wisselt tussen T1 en T2 (één meterfoto toont
 * dan maar één stand). De foto van de meter zelf blijft verplicht.
 */
function RegisterPhoto({
  name,
  photo,
  onPick,
  onRemove,
}: {
  name: string;
  photo: string | null;
  onPick: (f: File) => Promise<void>;
  onRemove: () => void;
}) {
  return (
    <div className="row meterphoto regphoto">
      {photo && (
        <a href={photoUrl(photo)} target="_blank" rel="noopener" className="meterthumb" title="Foto groot openen">
          <img src={thumbUrl(photo)} alt={`Foto telwerk ${name}`} />
        </a>
      )}
      <label className="btn small photobtn">
        <IconCamera size={18} aria-hidden="true" />
        {photo ? `Andere foto ${name}` : `Foto ${name} (optioneel)`}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          aria-label={`Foto telwerk ${name}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void onPick(f);
          }}
        />
      </label>
      {photo && (
        <button
          type="button"
          className="btn small ghost"
          onClick={onRemove}
          aria-label={`Foto telwerk ${name} weghalen`}
        >
          Weghalen
        </button>
      )}
    </div>
  );
}

/** Eén meter opnemen: standen, gebeurtenis, foto, controle en toelichting. */
function ReadingSheet({
  d,
  m,
  editable,
  onClose,
  onSaved,
}: {
  d: MeterRoundDetailDto;
  m: RoundMeterDto;
  editable: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const ops = useMeterRound(d.round.id, d.round.buildingId);
  const r = m.reading;
  const [readOn, setReadOn] = useState(r?.readOn ?? todayIso());
  const [event, setEvent] = useState<ReadingEvent>(r?.event ?? "normal");
  const [unreachable, setUnreachable] = useState(r?.unreachable ?? false);
  const [reason, setReason] = useState(r?.unreachableReason ?? "");
  const [photo, setPhoto] = useState<string | null>(r?.photoFileId ?? null);
  const [remark, setRemark] = useState(r?.remark ?? "");
  const [vals, setVals] = useState<Record<string, ValDraft>>(() =>
    Object.fromEntries(
      m.meter.registers.map((reg) => {
        const v = r?.values.find((x) => x.registerId === reg.id);
        const s = (n: number | null | undefined) => (n == null ? "" : String(n).replace(".", ","));
        return [
          reg.id,
          { value: s(v?.value), oldFinal: s(v?.oldFinal), newStart: s(v?.newStart), photo: v?.photoFileId ?? null },
        ];
      }),
    ),
  );
  const [busy, setBusy] = useState(false);
  const [finding, setFinding] = useState(false);
  // Meldingen bij het veld (in plaats van de browsermelding), na een poging tot opslaan.
  const [tried, setTried] = useState(false);
  const prev = m.history.at(-1) ?? null;
  const periods = useMemo(() => usagePeriods(m.meter, m.history), [m.meter, m.history]);

  const values = m.meter.registers
    .map((reg) => {
      const v = vals[reg.id]!;
      return {
        registerId: reg.id,
        value: parseNumber(v.value) ?? Number.NaN,
        oldFinal: event === "replaced" ? parseNumber(v.oldFinal) : null,
        newStart: event === "replaced" ? parseNumber(v.newStart) : null,
        photoFileId: v.photo,
      };
    })
    .filter((v) => vals[v.registerId]!.value.trim() !== "");
  const filled = values.length === m.meter.registers.length && values.every((v) => Number.isFinite(v.value));
  const check = useMemo(
    () =>
      unreachable || !values.length
        ? null
        : checkReading(
            { ...m.meter, assetId: m.asset.id },
            m.history,
            { readOn, unreachable, event, values },
            d.vacancies,
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(values), readOn, event, unreachable],
  );
  const errors =
    check && hasErrors(check) ? check.issues.filter((i) => i.severity === "error" && i.code !== "missing") : [];
  const warnings = check ? check.issues.filter((i) => i.severity === "warning") : [];
  const remarkNeeded = Boolean(check && needsRemark(check));
  const photoMissing = !unreachable && !photo;
  const remarkMissing = remarkNeeded && !remark.trim();

  /** Verwachte stand vóór het typen: vorige stand plus het verwachte verbruik tot de opnamedatum. */
  function expectedStand(registerId: string, prevValue: number): number | null {
    if (!prev || !readOn) return null;
    const days = daysBetween(prev.readOn, readOn);
    const perDay = days > 0 ? expectedPerDayFor(m.meter, periods, registerId, prev.readOn, readOn) : null;
    return perDay == null ? null : prevValue + (perDay * days) / (m.meter.factor || 1);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTried(true);
    const focus = (sel: string) => document.querySelector<HTMLElement>(`#readingform ${sel}`)?.focus();
    if (photoMissing) return focus(".photobtn input");
    if (!unreachable && !filled) return toast("Vul de stand van elk telwerk in");
    if (unreachable && !reason.trim()) return toast("Geef aan waarom de meter niet bereikbaar is");
    if (errors.length) return toast(errors[0]!.message);
    if (remarkMissing) return focus("textarea");
    if (!readOn) return toast("Vul de datum van de opname in");
    setBusy(true);
    try {
      const s = await ops.save(
        {
          id: r?.id ?? crypto.randomUUID(),
          assetId: m.asset.id,
          readOn,
          unreachable,
          unreachableReason: unreachable ? reason.trim() : null,
          photoFileId: photo,
          event: unreachable ? "normal" : event,
          remark: remark.trim() || null,
          values: unreachable ? [] : values,
        },
        `Meterstand ${m.asset.code || m.asset.name}`,
      );
      toast(
        s === "queued" ? "Stand bewaard op dit apparaat; wordt verstuurd zodra er verbinding is" : "Stand opgeslagen",
      );
      onSaved();
    } catch (err) {
      toast(errMsg(err));
      void qc.invalidateQueries({ queryKey: roundKey(d.round.id) });
    } finally {
      setBusy(false);
    }
  }

  async function makeFinding() {
    if (!r) return;
    setFinding(true);
    try {
      const res = await api<{ findingId: string; code: string }>(`/meters/readings/${r.id}/finding`, {
        method: "POST",
        body: {},
      });
      toast(`Bevinding ${res.code} gemaakt`);
      void qc.invalidateQueries({ queryKey: roundKey(d.round.id) });
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setFinding(false);
    }
  }

  return (
    <Modal
      title={`${m.asset.code} ${m.asset.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        editable ? (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Sluiten
            </button>
            <button type="submit" form="readingform" className="btn primary" disabled={busy}>
              Opslaan
            </button>
          </>
        ) : undefined
      }
    >
      <form id="readingform" className="stack readingform" onSubmit={submit} noValidate>
        <p className="small muted">
          {METER_TYPE_LABELS[m.meter.meterType]}
          {m.meter.unit && ` · ${m.meter.unit}`}
          {m.meter.ean && ` · EAN ${m.meter.ean}`}
          {m.meter.meterNumber && ` · nr. ${m.meter.meterNumber}`}
          {m.asset.floorName && ` · ${m.asset.floorName}`}
          {m.asset.roomName && `, ${m.asset.roomName}`}
          {m.vacantNow && " · staat leeg"}
        </p>
        <fieldset disabled={!editable} className="stack">
          <label className="check">
            <input type="checkbox" checked={unreachable} onChange={(e) => setUnreachable(e.target.checked)} />
            Niet bereikbaar
          </label>
          {unreachable ? (
            <label className="field">
              <span>Reden</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="bijv. kast op slot"
                maxLength={300}
              />
            </label>
          ) : (
            <>
              <div className="row meterphoto">
                {photo && (
                  <a
                    href={photoUrl(photo)}
                    target="_blank"
                    rel="noopener"
                    className="meterthumb"
                    title="Foto groot openen"
                  >
                    <img src={thumbUrl(photo)} alt="Foto van de meter" />
                  </a>
                )}
                <label className={`btn photobtn${photo ? "" : " primary"}`}>
                  <IconCamera size={20} aria-hidden="true" />
                  {photo ? "Andere foto" : "Foto van de meter"}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    aria-label="Foto van de meter"
                    aria-invalid={tried && photoMissing}
                    aria-describedby={tried && photoMissing ? "photo-err" : undefined}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      try {
                        setPhoto(await ops.photo(f));
                      } catch (err) {
                        toast(errMsg(err));
                      }
                    }}
                  />
                </label>
              </div>
              {tried && photoMissing && (
                <p className="note small error" role="alert" id="photo-err">
                  Maak een foto van de meter
                </p>
              )}
              {m.meter.registers.map((reg) => {
                const v = vals[reg.id]!;
                const pv = prev?.values.find((x) => x.registerId === reg.id);
                const expStand = pv ? expectedStand(reg.id, pv.value) : null;
                const set = (x: Partial<ValDraft>) => setVals({ ...vals, [reg.id]: { ...v, ...x } });
                const regIssues = check?.issues.filter((i) => i.registerId === reg.id && i.code !== "missing") ?? [];
                return (
                  <div key={reg.id} className="stack">
                    <label className="field">
                      <span>
                        {m.meter.registers.length > 1 ? reg.name : "Stand"}
                        {m.meter.unit && ` (${m.meter.unit})`}
                      </span>
                      <input
                        className="bignum"
                        inputMode="decimal"
                        autoComplete="off"
                        value={v.value}
                        onChange={(e) => set({ value: e.target.value })}
                        aria-describedby={`prev-${reg.id}`}
                      />
                      <span className="hint" id={`prev-${reg.id}`}>
                        {pv ? (
                          <>
                            Vorige {nl(pv.value)} op {fmtDate(prev!.readOn)}
                            {expStand != null && (
                              <>
                                {" · "}
                                <strong>verwacht rond {nl(expStand, 0)}</strong>
                              </>
                            )}
                          </>
                        ) : (
                          "Eerste opname van deze meter"
                        )}
                      </span>
                    </label>
                    {m.meter.registers.length > 1 && (
                      <RegisterPhoto
                        name={reg.name}
                        photo={v.photo}
                        onPick={async (f) => {
                          try {
                            const id = await ops.photo(f);
                            setVals((cur) => ({ ...cur, [reg.id]: { ...cur[reg.id]!, photo: id } }));
                          } catch (err) {
                            toast(errMsg(err));
                          }
                        }}
                        onRemove={() => setVals((cur) => ({ ...cur, [reg.id]: { ...cur[reg.id]!, photo: null } }))}
                      />
                    )}
                    {event === "replaced" && (
                      <div className="grid2">
                        <label className="field">
                          <span>Eindstand oude meter</span>
                          <input
                            inputMode="decimal"
                            value={v.oldFinal}
                            onChange={(e) => set({ oldFinal: e.target.value })}
                          />
                        </label>
                        <label className="field">
                          <span>Beginstand nieuwe meter</span>
                          <input
                            inputMode="decimal"
                            value={v.newStart}
                            onChange={(e) => set({ newStart: e.target.value })}
                          />
                        </label>
                      </div>
                    )}
                    {regIssues.map((i) => (
                      <p
                        key={i.code}
                        className={`note small ${i.severity === "error" ? "error" : "warn"}`}
                        role="alert"
                      >
                        {i.message}
                        {i.suggestion != null && (
                          <button
                            type="button"
                            className="btn small"
                            onClick={() => set({ value: String(i.suggestion).replace(".", ",") })}
                          >
                            Gebruik {nl(i.suggestion)}
                          </button>
                        )}
                      </p>
                    ))}
                    {check?.usage[reg.id] != null && !regIssues.length && (
                      <p className="small muted">
                        Verbruik {nl(check.usage[reg.id], 2)} {m.meter.unit} in {check.days} dagen
                      </p>
                    )}
                  </div>
                );
              })}
              <label className="field">
                <span>Bijzonderheid</span>
                <select value={event} onChange={(e) => setEvent(e.target.value as ReadingEvent)}>
                  {READING_EVENTS.map((x) => (
                    <option key={x} value={x}>
                      {READING_EVENT_LABELS[x]}
                    </option>
                  ))}
                </select>
              </label>
              {check?.issues
                .filter((i) => !i.registerId)
                .map((i) => (
                  <p key={i.code} className="note small warn" role="alert">
                    {i.message}
                  </p>
                ))}
            </>
          )}
          <label className="field">
            <span>Toelichting{remarkNeeded ? " (verplicht bij een afwijking)" : ""}</span>
            <textarea
              rows={2}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              maxLength={1000}
              aria-invalid={tried && remarkMissing}
              aria-describedby={tried && remarkMissing ? "remark-err" : undefined}
            />
            {tried && remarkMissing && (
              <span className="note small error" role="alert" id="remark-err">
                Geef een toelichting bij de afwijking
              </span>
            )}
          </label>
          <label className="field">
            <span>Datum opname</span>
            <input type="date" value={readOn} max={todayIso()} onChange={(e) => setReadOn(e.target.value)} required />
          </label>
        </fieldset>
        {warnings.length > 0 &&
          r &&
          !r.unreachable &&
          hasModule(me, "issues", "edit") &&
          (r.findingId ? (
            <Link className="small" to={`/gebouwen/${d.round.buildingId}?bevinding=${r.findingId}`}>
              Bevinding {r.findingCode ?? ""} bekijken
            </Link>
          ) : (
            <button type="button" className="btn small" disabled={finding} onClick={() => void makeFinding()}>
              Bevinding maken
            </button>
          ))}
      </form>
    </Modal>
  );
}
